import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addRow, getAllSheets, saveSheet } from "../Redux/Actions/TechnicalSheet";
import { getFamily } from "../Redux/Actions/IngredientActions";
import axios from "axios";
import "./TechnicalSheet.css";

function SubTechnicalSheet() {
  const dispatch = useDispatch();
  const families = useSelector((state) => state.IngredientReducer?.families || []);

  const [subSheetName, setSubSheetName] = useState("");
  const [selectedFamily, setSelectedFamily] = useState("");
  const [selectedIngredient, setSelectedIngredient] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [pricePerUnit, setPricePerUnit] = useState(0);
  const [sheetRows, setSheetRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState([]);

  useEffect(() => {
    dispatch(getFamily());
    dispatch(getAllSheets());
    
    const fetchStockData = async () => {
      try {
        const response = await axios.get("/api/Invoices/stock-report");
        setStockData(response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération des données du stock", error);
      }
    };

    fetchStockData();
  }, [dispatch]);

  const getAveragePrice = (ingredientId) => {
    const stockItem = stockData.find(item => item.ingredient === ingredientId);
    return stockItem ? stockItem.averagePrice : 0;
  };

  const getIngredientNameById = (ingredient) => {
    if (!ingredient) return "Inconnu";
    
    if (typeof ingredient === 'object' && ingredient.name) {
      return ingredient.name;
    }
    
    if (typeof ingredient === 'string') {
      for (const family of families) {
        const found = family?.ingredients?.find(ing => 
          ing._id === ingredient || ing.name === ingredient
        );
        if (found) return found.name;
      }
    }
    
    return "Inconnu";
  };


  const addRowHandler = () => {
    if (!selectedIngredient || quantity <= 0 || pricePerUnit <= 0) {
      alert("Veuillez remplir tous les champs requis.");
      return;
    }

    const familyObj = families.find((family) => family.name === selectedFamily);
    const ingredientObj = familyObj?.ingredients.find((ingredient) => ingredient.name === selectedIngredient);

    if (!ingredientObj) {
      alert("Ingrédient non trouvé.");
      return;
    }

    const cost = quantity * pricePerUnit;
    const newRow = {
      ingredient: ingredientObj._id,
      family: familyObj._id,
      quantity: parseFloat(quantity),
      pricePerUnit: parseFloat(pricePerUnit),
      cost: parseFloat(cost.toFixed(2)),
    };

    setSheetRows((prevRows) => [...prevRows, newRow]);
    setSelectedIngredient("");
    setQuantity(0);
    setPricePerUnit(0);
  };

  const deleteRowHandler = (index) => {
    setSheetRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  const totalCost = sheetRows.reduce((acc, row) => acc + (row?.cost || 0), 0).toFixed(2);

  const savePageHandler = async () => {
    if (!subSheetName || sheetRows.length === 0) {
      alert("Veuillez remplir toutes les informations nécessaires avant de sauvegarder.");
      return;
    }

    const sheetSubData = {
      nomSub: subSheetName,
      rows: sheetRows.map((row) => ({
        ...row,
        total: row.cost,
      })),
      totalCost: parseFloat(totalCost),
    };

    setLoading(true);
    try {
      await axios.post("/api/SubTechnicalSheet/save-Subsheet", sheetSubData);
      alert("Sous-fiche technique sauvegardée avec succès !");
      setSubSheetName("");
      setSheetRows([]);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde :", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="technical-sheet">
      <h2>Sous-Fiche Technique</h2>
      <div className="form-section">
        <input
          type="text"
          value={subSheetName}
          onChange={(e) => setSubSheetName(e.target.value)}
          placeholder="Nom de la sous-fiche"
        />
      </div>
      <div className="form-row">
        <select value={selectedFamily} onChange={(e) => setSelectedFamily(e.target.value)}>
          <option value="">-- Sélectionnez une famille --</option>
          {families.map((family) => (
            <option key={family._id} value={family.name}>{family.name}</option>
          ))}
        </select>

        <select 
          value={selectedIngredient} 
          onChange={(e) => {
            setSelectedIngredient(e.target.value);
            if (e.target.value && selectedFamily) {
              const familyObj = families.find((family) => family.name === selectedFamily);
              const ingredientObj = familyObj?.ingredients.find((ingredient) => ingredient.name === e.target.value);
              if (ingredientObj) {
                const avgPrice = getAveragePrice(ingredientObj._id);
                setPricePerUnit(avgPrice.toFixed(3));
              }
            }
          }} 
          disabled={!selectedFamily}
        >
          <option value="">-- Sélectionnez un ingrédient --</option>
          {selectedFamily && families.find((family) => family.name === selectedFamily)?.ingredients.map((ingredient) => (
            <option key={ingredient._id} value={ingredient.name}>{ingredient.name}</option>
          ))}
        </select>
      </div>
      <div className="form-row">
        <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} min="0" placeholder="Quantité" />
        <input 
          type="number" 
          value={pricePerUnit} 
          onChange={(e) => setPricePerUnit(e.target.value)} 
          min="0" 
          placeholder="Prix unitaire" 
        />
      </div>
      <button onClick={addRowHandler}>Ajouter à la sous-fiche</button>
      <table className="sheet-table">
        <thead>
          <tr>
            <th>Ingrédient</th>
            <th>Quantité</th>
            <th>Prix unitaire (DT)</th>
            <th>Coût (DT)</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {sheetRows.map((row, index) => (
            <tr key={index}>
              <td>{getIngredientNameById(row.ingredient)}</td>
              <td>{row.quantity}</td>
              <td>{row.pricePerUnit.toFixed(3)}</td>
              <td>{row.cost.toFixed(3)}</td>
              <td><button onClick={() => deleteRowHandler(index)}>Supprimer</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="total">
        <strong>Total : {totalCost} DT</strong>
      </div>
      <button className="save-button" onClick={savePageHandler} disabled={loading}>
        {loading ? "Enregistrement..." : "Enregistrer la sous-fiche"}
      </button>
    </div>
  );
}

export default SubTechnicalSheet;